/**
 * Custom events for Google Analytics. A no-op unless analytics is allowed and
 * live on this page (see lib/tracking.ts), so an event fired before consent is
 * dropped rather than queued for later.
 */

import { analyticsActive } from './tracking';

/**
 * GA4 takes only letters, digits and underscores in an event name, and the
 * funnel's names were written for Umami with hyphens, so `funnel-cta-click`
 * arrives in Google Analytics as `funnel_cta_click`.
 */
export function gaEventName(event: string): string {
  return event.replace(/[^A-Za-z0-9_]/g, '_');
}

type EventParams = Record<string, string | number | boolean>;

const EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const PHONE = /^\+?[\d\s().-]{7,}$/;

/**
 * GA's own names for where a session came from. An event parameter called
 * `source` is read as the traffic source: the newsletter form under every
 * article sent `source: 'blog-post'`, and GA filed those sessions under a
 * source called "blog-post" with no medium instead of Google search. Name a
 * placement `placement`.
 */
const TRAFFIC_SOURCE_KEYS = new Set([
  'source',
  'medium',
  'campaign',
  'term',
  'content',
  'campaign_id',
  'campaign_source',
  'campaign_medium',
  'campaign_name',
  'campaign_term',
  'campaign_content',
]);

/**
 * Nothing a visitor typed belongs in an event, and no caller sends any. This is
 * the backstop: a value that looks like an email address or a phone number is
 * dropped whatever the caller passed, and so is a parameter that would rewrite
 * the session's traffic source.
 */
export function eventParams(data?: EventParams): EventParams | undefined {
  if (!data) return undefined;
  const kept: EventParams = {};
  for (const [key, value] of Object.entries(data)) {
    if (TRAFFIC_SOURCE_KEYS.has(key)) continue;
    if (typeof value === 'string' && (EMAIL.test(value) || PHONE.test(value.trim()))) continue;
    kept[key] = value;
  }
  return kept;
}

export function trackEvent(event: string, data?: EventParams) {
  try {
    if (!analyticsActive()) return;
    window.gtag?.('event', gaEventName(event), eventParams(data));
  } catch {
    // silently ignore — analytics should never break the app
  }
}
